/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
export const locales = { lt: 'lt', en: 'en' } as const
export type Locales = keyof typeof locales

export const STORE_LANGUAGES = [
  { name: 'Lietuvių', iso: locales.lt },
  { name: 'English', iso: locales.en },
] as const

const localeState = () => useState('auth:locale', () => shallowRef<{ lang: Locales | undefined }>({ lang: undefined }))

export function useLocaleState(): {
  set(lang: Locales): void
  locale: globalThis.ComputedRef<Locales | undefined>
} {
  const locale = localeState()
  return {
    locale: computed(() => locale.value.lang),
    set(lang: Locales): void {
      if (locale.value.lang !== lang) locale.value = { lang }
    },
  }
}

export function useLocale(): globalThis.ComputedRef<Locales> {
  const lang = localeState()
  return computed(() => lang.value.lang || locales.en)
}

type stringI18nInput = string | TemplateStringsArray | None | number

function isTemplateStringsArray(value: unknown): value is TemplateStringsArray {
  return Array.isArray(value) && 'raw' in value && typeof value.raw !== 'undefined' && Array.isArray(value.raw) && Object.isFrozen(value) && Object.isFrozen(value.raw)
}
function toStr(val: stringI18nInput): string | undefined {
  if (isTemplateStringsArray(val)) return val.join('')
  if (typeof val === 'number') return val.toString()
  return val ?? undefined
}

export function ts(str1: stringI18nInput, ...rest: stringI18nInput[]): stringI18n {
  if (typeof str1 === 'string' && !rest.length) return str1 as stringI18n
  const str = toStr(str1)
  if (!rest?.length) return (str ?? '') as stringI18n
  const add = helpers.joinStringCb(' ', rest, (i) => toStr(i))
  if (add?.length && str?.length) return (str + ' ' + add) as stringI18n
  if (str?.length) return str as stringI18n
  if (add?.length) return add as stringI18n
  return '' as stringI18n
}

export type T2Translation = string | Record<Locales, string>

export function useT2Create<TGlobal extends Record<string, T2Translation>>(translations: TGlobal) {
  // const isDev = import.meta.dev
  // const missing: Record<string, string> = {}

  const getT = (i: string, obj: Record<string, T2Translation>, lang: Locales): stringI18n | undefined => {
    const res = i && i in obj ? obj[i as unknown as keyof typeof obj] : undefined
    if (typeof res === 'object' && lang in res) return res[lang] as stringI18n
    if (lang === 'en') return i as stringI18n
    if (typeof res === 'string') return (res === 'todo' ? i : res) as stringI18n
  }

  function useT2<T extends { [key in keyof T]: key extends keyof TGlobal ? never : T2Translation }>(lt2: T) {
    const locale = useLocale()

    return (i2: keyof T | keyof TGlobal, langFb?: Locales): stringI18n => {
      const key = i2.toString()
      const lang = langFb || locale.value
      const found = getT(key, lt2, lang) ?? (getT(key, translations, lang) as stringI18n | undefined)
      if (found) return found
      return key as stringI18n
    }
  }

  return useT2
}

export const useT2Base = useT2Create({})

// Using Intl.DateTimeFormat for language-sensitive date and time formatting
// Learn more: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat

// export function useLocaleDate(date: Ref<Date> | Date, locale = useLocale()) {
//   return computed(() => new Intl.DateTimeFormat(locale.value, { dateStyle: 'full' }).format(unref(date)))
// }
